<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import BucketUnits from "@/modules/dimension_values/components/BucketUnits.vue";
import SelectPaletteDialog from "@/ui/components/colorPicker/SelectPaletteDialog.vue";
import { getColorsForPalette } from "@/ui/components/colorPicker/colorDefinitions";
import { useApiClient } from "@/api/client";
import DimensionDO = App.Data.DimensionDO;
import BucketValuePutDTO = App.Data.BucketValuePutDTO;
import {
	randomizeColor,
	randomizeColors,
	createBucketFromConfig,
	generateLabel,
} from "@/modules/dimension_values/components/BucketCreation";
import BucketCreationDTO = App.Data.BucketCreationDTO;
import TableBucketSettings from "@/modules/dimension_values/components/TableBucketSettings.vue";
import BucketPutDTO = App.Data.BucketPutDTO;
import {
	maxLength,
	minLength,
	required,
} from "@/ui/validation/validationRules";
import { useCloned } from "@vueuse/core";

const { t } = useI18n();

const loading = defineModel<boolean>("loading");
const bucket = defineModel<BucketPutDTO | null>("bucket");
const { cloned: clonedBucket, sync: syncBucket } = useCloned(bucket, {
	manual: true,
});
const props = defineProps<{
	dimension: DimensionDO;
	isFormValid: boolean;
}>();
const emit = defineEmits(["save", "setAsDefault", "delete"]);

const totalItems = ref(10);
const itemsPerPage = ref(25);
const search = ref("");
const paletteColors = ref<string[]>([]);
const showPaletteDialog = ref(false);
const useSelectedFirstColor = ref<null | string>(null);
const userSelectedFirstColorFromPaletteUuid = ref<null | string>(null);

const defineUserSelectedOptions = (paletteUuid: string, hexColor: string) => {
	userSelectedFirstColorFromPaletteUuid.value = paletteUuid;
	useSelectedFirstColor.value = hexColor;
};

const resetUserSelectedOptions = () => {
	userSelectedFirstColorFromPaletteUuid.value = null;
	useSelectedFirstColor.value = null;
};

onMounted(async () => {
	const response = await useApiClient("environment").getColorPalettes();
	if (response.isOk()) {
		const defaultPalette =
			response.value.find((p) => p.is_default) ?? response.value[0];
		if (defaultPalette?.uuid) {
			paletteColors.value = await getColorsForPalette(defaultPalette.uuid);
		}
	}
});

const onPaletteSelected = async (uuid: string, startHexColor?: string) => {
	paletteColors.value = await getColorsForPalette(uuid);
	const foundIndex = startHexColor
		? paletteColors.value.indexOf(startHexColor)
		: -1;
	randomizeColors(
		bucket.value.values,
		paletteColors.value,
		foundIndex !== -1 ? foundIndex : undefined
	);
	resetUserSelectedOptions();
};

const visibleHeaders = [
	{
		title: t("dimension_values.table.start_value"),
		key: "start_value",
		align: "start",
		sortable: false,
	},
	{
		title: t("dimension_values.table.header"),
		key: "label",
		align: "start",
		sortable: false,
	},
	{
		title: t("dimension_values.table.color"),
		key: "color",
		align: "center",
		sortable: false,
	},
	{
		title: t("dimension_values.table.actions"),
		key: "actions",
		align: "end",
		sortable: false,
	},
];

const INT32_MIN = -2_147_483_648; // -(1 << 31)

const duplicateLabels = computed(
	(): { type: string; index1: number; index2: number } | null => {
		const seenStartValue = new Map<number, number>();
		const seenLabel = new Map<string, number>();

		for (let i = 0; i < bucket.value.values.length; i++) {
			const currentStartValue = bucket.value.values[i].start_value;
			const currentLabel = bucket.value.values[i].label;

			if (seenStartValue.has(currentStartValue)) {
				return {
					type: "start value",
					index1: seenStartValue.get(currentStartValue),
					index2: i,
				};
			}
			if (seenLabel.has(currentLabel)) {
				return {
					type: "label",
					index1: seenLabel.get(currentLabel),
					index2: i,
				};
			}
			seenStartValue.set(currentStartValue, i);
			seenLabel.set(currentLabel, i);
		}
		return null;
	}
);

const duplicateLabelsError = computed((): string | null => {
	if (duplicateLabels.value === null) {
		return null;
	}
	return `Duplicate ${duplicateLabels.value.type} at position ${
		duplicateLabels.value.index1 + 1
	} and ${duplicateLabels.value.index2 + 1}`;
});

const isBucketDirty = computed(() => {
	const bucketToJson = (bucket: BucketPutDTO) =>
		JSON.stringify({
			...bucket,
			bucket: { ...bucket.bucket, is_default: undefined },
		});

	return bucketToJson(bucket.value) !== bucketToJson(clonedBucket.value);
});

watch(loading, (val) => {
	if (!val) syncBucket();
});

function reloadLabel(index: number) {
	bucket.value.values[index].label = generateLabel(
		bucket.value.values[index].start_value,
		bucket.value.values[index + 1]?.start_value ?? null,
		index === 0,
		props.dimension,
		t
	);
}

async function reloadLabels() {
	for (let i = 0; i < bucket.value.values.length; i++) {
		reloadLabel(i);
	}
}

function getBucketIndex(selectedBucket: BucketValuePutDTO): number {
	return bucket.value.values.findIndex((b) => b === selectedBucket);
}

// A safe way to get the difference in value between bucket n and n+1
// Edge cases:
// One of the buckets doesn't exist -> null
// The first bucket is INT32_MIN -> 1
function getBucketDelta(
	selectedBucket: BucketValuePutDTO | number
): number | null {
	if (typeof selectedBucket === "object") {
		selectedBucket = getBucketIndex(selectedBucket);
	}
	if (selectedBucket < 0 || selectedBucket + 1 >= bucket.value.values.length) {
		return null;
	}
	const [a, b] = [
		bucket.value.values[selectedBucket].start_value === INT32_MIN
			? 0
			: bucket.value.values[selectedBucket].start_value,
		bucket.value.values[selectedBucket + 1].start_value,
	];
	return Math.max(1, b - a);
}

function removeValue(toDelete: BucketValuePutDTO) {
	const prevIndex = getBucketIndex(toDelete) - 1;

	if (toDelete.dimension_value_id) {
		bucket.value.deletions.push(toDelete.dimension_value_id);
	}
	bucket.value.values = bucket.value.values.filter((obj) => obj !== toDelete);

	if (prevIndex < 0) return;
	if (bucket.value.bucket.should_auto_label) {
		reloadLabel(prevIndex);
	}
}

function addValue(toCopy: BucketValuePutDTO) {
	const index = getBucketIndex(toCopy);
	if (index < 0) return;

	let newStartValue = Math.floor(
		toCopy.start_value +
			(index === bucket.value.values.length - 1
				? getBucketDelta(index - 1) ?? 1 // Get delta of 2 values below
				: (getBucketDelta(index) ?? 2) / 2) // Get delta of values above and below, and divide by 2)
	);
	if (
		toCopy.start_value === INT32_MIN &&
		(bucket.value.values.length <= 1 ||
			bucket.value.values[index + 1]?.start_value > 1)
	) {
		newStartValue = Math.max(1, newStartValue);
	}

	const newLabel = generateLabel(
		newStartValue,
		bucket.value.values[index + 1]?.start_value ?? null,
		false,
		props.dimension,
		t
	);
	const newValue = {
		dimension_value_id: null,
		sort_order: toCopy.sort_order + 1,
		start_value: newStartValue,
		label: newLabel,
		hex_color_value: randomizeColor(paletteColors.value),
	};
	bucket.value.values.splice(index + 1, 0, newValue);
	if (bucket.value.bucket.should_auto_label) {
		reloadLabel(index);
	}
}

function batchAddValues(itemCount: number, itemRange: number) {
	// todo: maybe add delta suggestion
	const fakeDTO: BucketCreationDTO = {
		name: "",
		start_value:
			bucket.value.values[bucket.value.values.length - 1].start_value +
			itemRange,
		item_count: itemCount,
		gap_size: itemRange,
	};
	const toAppend = createBucketFromConfig(fakeDTO, props.dimension, t, true);
	for (const item of toAppend) {
		item.hex_color_value = randomizeColor(paletteColors.value);
	}
	const lastIndex = bucket.value.values.length - 1;
	bucket.value.values.push(...toAppend);
	if (bucket.value.bucket.should_auto_label) {
		reloadLabel(lastIndex);
	}
}

watch(
	bucket,
	() => {
		bucket.value.values.sort((a, b) => a.start_value - b.start_value);

		if (bucket.value.bucket.should_auto_label) {
			reloadLabels();
		}
	},
	{ deep: true }
);

function toggleAutoLabels() {
	bucket.value.bucket.should_auto_label =
		!bucket.value.bucket.should_auto_label;
	if (bucket.value.bucket.should_auto_label) {
		reloadLabels();
	}
}

function toggleInvertedSortOrder() {
	bucket.value.bucket.has_inverted_sort_order =
		!bucket.value.bucket.has_inverted_sort_order;
}
</script>

<template>
	<select-palette-dialog
		v-if="showPaletteDialog"
		v-model="showPaletteDialog"
		:initial-palette-uuid="userSelectedFirstColorFromPaletteUuid"
		:initial-hex-color="useSelectedFirstColor"
		@user-selects-palette="onPaletteSelected"
	/>

	<v-alert
		v-if="duplicateLabelsError !== null"
		class="mb-4"
		title="Duplicate labels"
		:text="duplicateLabelsError"
		type="error"
	/>
	<div class="action-bar">
		<v-text-field
			v-model="bucket.bucket.name"
			label="Bucket Name"
			hide-details
			:rules="[required(t), minLength(t, 3), maxLength(t, 128)]"
		>
			<v-tooltip
				text="Shown as the default bucket for this dimension in the explorer"
			>
				<template v-slot:activator="{ props }">
					<v-icon
						v-if="bucket.bucket.is_default"
						v-bind="props"
						size="18"
						class="ml-n1 me-1"
						icon="mdi-star-outline"
					/>
				</template>
			</v-tooltip>
		</v-text-field>
		<v-badge
			:model-value="isBucketDirty"
			color="red"
			offset-x="4"
			offset-y="4"
			dot
		>
			<v-btn
				:disabled="loading || !isFormValid || duplicateLabelsError !== null"
				height="48"
				text="Save"
				prepend-icon="mdi-content-save"
				@click="emit('save')"
			/>
		</v-badge>
		<table-bucket-settings
			:loading="loading"
			:dimension="dimension"
			:bucket="bucket"
			@batch-add-values="batchAddValues"
			@toggle-auto-label="toggleAutoLabels"
			@toggle-inverted-sort-order="toggleInvertedSortOrder"
			@randomize-colors="showPaletteDialog = true"
			@set-as-default="emit('setAsDefault')"
			@delete="emit('delete')"
		/>
	</div>

	<v-data-table
		v-model:items-per-page="itemsPerPage"
		:sort-by="[
			{
				key: 'start_value',
				order: bucket.bucket.has_inverted_sort_order ? 'desc' : 'asc',
			},
		]"
		density="comfortable"
		class="mt-2"
		:headers="visibleHeaders as any"
		:items="bucket.values"
		:items-length="totalItems"
		:loading="loading"
		:search="search"
		item-value="name"
	>
		<template v-slot:loading>
			<v-skeleton-loader type="table-row@5"></v-skeleton-loader>
		</template>
		<template v-slot:item="{ item, index }">
			<tr>
				<td v-if="item.start_value <= -10_000_000">-∞</td>
				<td v-else>
					<bucket-units
						v-model:value="item.start_value"
						:disabled="getBucketIndex(item) === 0"
						:unit="props.dimension?.unit"
						size="compact"
					/>
				</td>
				<td>
					<v-tooltip
						:disabled="!bucket.bucket.should_auto_label"
						text="Disable auto labeling to edit labels manually"
						interactive
						open-delay="200"
						close-delay="400"
					>
						<template #activator="{ props }">
							<input
								v-bind="props"
								type="text"
								maxlength="255"
								v-model.lazy="item.label"
								:disabled="bucket.bucket.should_auto_label"
							/>
						</template>
						<div>
							<span
								class="text-decoration-underline cursor-pointer"
								@click="bucket.bucket.should_auto_label = false"
							>
								Disable auto labeling
							</span>
							to edit labels manually
						</div>
					</v-tooltip>
				</td>
				<td style="width: 30px">
					<color-picker
						v-model="item.hex_color_value"
						:on-color-click="index === 0 ? defineUserSelectedOptions : null"
						:show-locked-onto-for-randomize="
							useSelectedFirstColor === item.hex_color_value && index === 0
						"
					/>
				</td>
				<td style="text-align: right" class="text-no-wrap">
					<v-btn
						class="mr-2"
						density="compact"
						variant="plain"
						icon="mdi-plus"
						color="on-surface"
						@click="addValue(item)"
						:disabled="(getBucketDelta(item) ?? 2) <= 1"
					/>
					<v-btn
						class="mr-n2"
						density="compact"
						variant="plain"
						icon="mdi-delete"
						color="error"
						@click="removeValue(item)"
						:disabled="bucket.values.length <= 2 || getBucketIndex(item) === 0"
					/>
				</td>
			</tr>
		</template>
	</v-data-table>
</template>

<style scoped>
.action-bar {
	display: grid;
	gap: 1rem;
	grid-template-columns: 1fr auto auto;

	@media only screen and (max-width: 480px) {
		grid-template-columns: 1fr;
		grid-template-rows: repeat(3, 1fr);
	}
}

input[type="text"] {
	border: 1px solid rgb(var(--color-secondary));
	border-radius: 4px;
	padding: 0 6px;
	background-color: rgba(var(--color-background));
}

input[type="text"]:disabled {
	color: rgba(var(--color-on-surface), 0.5);
}

input[type="color"] {
	appearance: none;
	height: 1.25rem;
	width: 2rem;
	background-color: transparent;

	&::-webkit-color-swatch-wrapper {
		padding: 0;
	}

	&::-webkit-color-swatch {
		border: 1px solid;
		border-radius: 4px;
	}
}
</style>
